import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ICertificate, ILearnerSummary } from '@stem/contracts';
import { apiClient } from '../services/api';
import { EmptyState, Meter, Section, Stat } from './parts';
import { useSession } from '../store/session';

/** The default dashboard: a teacher doing CPD, or a Grade 7 learner. */
export function LearnerDashboard() {
  const { orgSlug = '' } = useParams();
  const profile = useSession(state => state.profile);

  const summary = useQuery({
    queryKey: ['learn-summary'],
    queryFn: () => apiClient.get<ILearnerSummary>('/v1/learn/summary'),
  });

  const certificates = useQuery({
    queryKey: ['my-certificates'],
    queryFn: () => apiClient.get<ICertificate[]>('/v1/certificates/mine'),
  });

  const data = summary.data;

  return (
    <div>
      <h1 className="text-2xl font-bold text-ink-900">
        {profile?.persona === 'student' ? 'My learning' : 'My professional learning'}
      </h1>
      <p className="text-sm text-ink-600">Pick up where you left off.</p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Courses in progress" value={data?.inProgress ?? '—'} />
        <Stat label="Completed" value={data?.completed ?? '—'} tone="good" />
        <Stat label="Certificates" value={data?.certificates ?? '—'} />
        <Stat
          label="Time learning"
          value={data ? `${Math.round(data.minutesLearned)}m` : '—'}
          hint="Recorded from your own activity"
        />
      </div>

      <Section title="Continue" action={{ label: 'All courses', to: `/o/${orgSlug}/courses` }}>
        {data && data.recent.length > 0 ? (
          <ul className="space-y-2">
            {data.recent.map(row => (
              <li key={row.courseId} className="card flex flex-wrap items-center gap-3">
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink-900">
                  {row.courseTitle}
                </span>
                <Meter value={row.progressPercent} />
                <Link to={`/o/${orgSlug}/courses`} className="text-xs text-brand-700 underline">
                  Open
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState>
            Nothing started yet.{' '}
            <Link to={`/o/${orgSlug}/courses`} className="text-brand-700 underline">
              Browse the catalogue
            </Link>{' '}
            or{' '}
            <Link to={`/o/${orgSlug}/labs`} className="text-brand-700 underline">
              open a virtual laboratory
            </Link>
            .
          </EmptyState>
        )}
      </Section>

      {certificates.data && certificates.data.length > 0 && (
        <Section title="My certificates" action={{ label: 'All', to: `/o/${orgSlug}/certificates` }}>
          <ul className="grid gap-2 sm:grid-cols-2">
            {certificates.data.slice(0, 4).map(certificate => (
              <li key={certificate.id} className="card">
                <p className="text-sm font-medium text-ink-900">{certificate.courseTitle}</p>
                <p className="font-mono text-xs text-ink-500">{certificate.serial}</p>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}
